import { Fragment, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { css } from '@emotion/react';
import { useQueryClient } from 'react-query';

import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { useSaveQueueGroup } from '../api/useQueueGroupController';
import { QueueType } from '../models/enums/QueueType';
import { PermissionType } from '../../../features/seismic/models/enums/PermissionType';

const contentStyle = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    padding: 24,
    width: '100%',
    boxSizing: 'border-box',
    '& .create-group-field-label': {
        marginBottom: 6,
    },
    '& .create-group-actions': {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        marginTop: 8,
    },
});

const PERMISSION_OPTIONS: { value: PermissionType; label: string }[] = [
    { value: PermissionType.Private, label: 'Private' },
    { value: PermissionType.InCompany, label: 'In Company' },
];

const QUEUE_TYPE_OPTIONS: { value: QueueType; label: string }[] = [
    { value: QueueType.UploadSeismic2D, label: 'Seismic 2D' },
    { value: QueueType.UploadSeismic3D, label: 'Seismic 3D' },
    { value: QueueType.UploadShapefile, label: 'Shapefile' },
    { value: QueueType.UploadTiff, label: 'TIFF' },
    { value: QueueType.UploadGrid, label: 'Grid' },
    { value: QueueType.CompositeProfile, label: 'Composite Profile' },
].sort((a, b) => a.label.localeCompare(b.label));

type EditGroup = {
    token: string;
    name: string;
    queueType: QueueType;
    permission: PermissionType;
};

interface IQueueProcessingCreateGroupPopupProps {
    open: boolean;
    onChangeOpen: (value: boolean) => void;
    onSaved: (token: string) => void;
    editGroup?: EditGroup;
}

export function QueueProcessingCreateGroupPopup({
    open,
    onChangeOpen,
    onSaved,
    editGroup,
}: IQueueProcessingCreateGroupPopupProps) {
    const isEditMode = !!editGroup;

    const [name, setName] = useState('');
    const [queueType, setQueueType] = useState<QueueType>(QUEUE_TYPE_OPTIONS[0].value);
    const [permission, setPermission] = useState<PermissionType>(PermissionType.Private);

    const queryClient = useQueryClient();
    const { mutate: saveGroup, isLoading } = useSaveQueueGroup();

    useEffect(() => {
        if (open) {
            setName(editGroup?.name ?? '');
            setQueueType(editGroup?.queueType ?? QUEUE_TYPE_OPTIONS[0].value);
            setPermission(editGroup?.permission ?? PermissionType.Private);
        }
    }, [open, editGroup]);

    function handleClose() {
        if (isLoading) {
            return;
        }
        onChangeOpen(false);
    }

    function handleQueueTypeChange(event: SelectChangeEvent<QueueType>) {
        setQueueType(event.target.value as QueueType);
    }

    function handleSave() {
        if (!name.trim()) {
            return;
        }
        saveGroup(
            { token: editGroup?.token, name: name.trim(), queueType, permission },
            {
                onSuccess: (result) => {
                    queryClient.invalidateQueries(['QueueGroupsByUser']);
                    if (isEditMode) {
                        queryClient.invalidateQueries(['QueueGroupById', editGroup.token]);
                    }
                    handleClose();
                    onSaved(result.Token);
                },
            }
        );
    }

    return (
        <GeopostPopup
            open={open}
            onChangeOpen={handleClose}
            title={isEditMode ? 'Edit Batch' : 'New Batch'}
            width={440}
            height={380}
            resizable={false}
            draggable
            overlay
        >
            <Fragment>
                <div css={contentStyle}>
                    <Box>
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 600, color: '#424242' }}
                            className='create-group-field-label'
                        >
                            Name
                        </Typography>
                        <TextField
                            size='small'
                            placeholder='Enter batch name...'
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isLoading}
                            fullWidth
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSave();
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 600, color: '#424242' }}
                            className='create-group-field-label'
                        >
                            Queue Type
                        </Typography>
                        <Select
                            size='small'
                            value={queueType}
                            onChange={handleQueueTypeChange}
                            disabled={isLoading || isEditMode}
                            fullWidth
                        >
                            {QUEUE_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 600, color: '#424242' }}
                            className='create-group-field-label'
                        >
                            Permission
                        </Typography>
                        <Select
                            size='small'
                            value={permission}
                            onChange={(e) => setPermission(e.target.value as PermissionType)}
                            disabled={isLoading}
                            fullWidth
                        >
                            {PERMISSION_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <div className='create-group-actions'>
                        <Button
                            variant='outlined'
                            size='small'
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            size='small'
                            onClick={handleSave}
                            disabled={isLoading || !name.trim()}
                            startIcon={isLoading ? <CircularProgress size={14} color='inherit' /> : undefined}
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
            </Fragment>
        </GeopostPopup>
    );
}
